import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AnnotationViewContainer — mirrors `BlockViewContainer` exactly.
 *
 * `BlockViewContainer` resolves a `ContentComponent` from
 * `contentRegistry` per block and hands it to `<BlockView>`.
 * `AnnotationViewContainer` does the same per row of a block:
 * subscribes to `userAnnotationStore.useByBlock(blockId)` once,
 * resolves a registry entry per row, and renders one
 * `<AnnotationView>` per row.
 *
 * One subscription per block, one DOM subtree, one rect origin.
 * Per-type rendering is delegated to the registry's `Component`s,
 * which paint the actual sticker / drawing / highlight / pin.
 *
 * Mounting:
 *   - "expanded" — inside the BlockDetailModal's panel. Container's
 *                  rect is the panel's rect; gestures enabled.
 *   - "regular"  — inside a stream card's host. Container's rect is
 *                  the card's rect; gestures disabled.
 *
 * In both modes the container is a sibling of `.block-view`, never
 * inside it. The block view sets `overflow: hidden` (rounded card
 * corners), which would clip any annotation that legitimately extends
 * past the card edge — sticker overshoot, drawing-stroke flicks,
 * stamp anchors near the card corner. Keeping the container outside
 * the block view lets every annotation paint into the same
 * `[-0.1, 1.1]` overshoot band.
 */
import { memo, useCallback, useMemo, useRef } from "react";
import { userAnnotationStore } from "@/data/user-annotation-store";
import { OPTIMISTIC_OWNER_PLACEHOLDER } from "@/data/user-annotation-optimistic-id";
import { useCurrentAccountId } from "@/hooks/use-current-account-id";
import { AnnotationView } from "./annotation-view";
import { defaultPositionFor, resolveAnnotationEntry, } from "./annotation-registry";
import { DrawingSelectionView } from "./views/drawing-selection-view";
import { resolveDrawingFrame, } from "./views/drawing-transform";
import "./annotation.scss";
// True when the annotation belongs to the signed-in account. Treat
// the optimistic owner placeholder as "mine" so a user can drag /
// resize / rotate a mark they just placed without waiting for the
// server to stamp the real `ownerId` — only the creating client ever
// writes that placeholder, so it's unambiguous.
//
// Returns `false` while `currentAccountId` is still resolving (the
// hook returns `undefined` for a few ms after modal open until the
// identity slot fills) — same conservative bias the comment delete
// button uses: hide the affordance during the unknown window rather
// than flicker it on/off.
function isAnnotationMine(annotation, currentAccountId) {
    if (annotation.ownerId === OPTIMISTIC_OWNER_PLACEHOLDER) {
        return true;
    }
    if (currentAccountId === undefined)
        return false;
    return annotation.ownerId === currentAccountId;
}
export const AnnotationViewContainer = memo(function AnnotationViewContainer({ blockId, mode, extraAnnotations, positions, gestureHandlers, liveOverlay, drawingSelection, markSelection, }) {
    // The container's own div is the host whose rect every annotation
    // resolves against. Gesture plumbing reads this on every pointer-move
    // so the math survives mid-drag size changes (e.g. modal still
    // finishing FLIP grow). Marked HTMLDivElement | null to satisfy
    // both the gesture handlers and the overlay views.
    const containerRef = useRef(null);
    // Signed-in account id, used to gate per-annotation interactivity to
    // the row's owner. Non-owner drawings / stamps / stickers / emoji
    // reactions render as decorative paint — no selection chrome, no
    // drag, no resize/rotate, no delete affordance — mirroring the
    // ownership semantics the comment popover / delete button already
    // enforce on comments.
    const currentAccountId = useCurrentAccountId();
    // Single retrieval: all rows for this block, once, from the store.
    // Both expanded and regular consumers share this subscription
    // through the TanStack DB collection's live query.
    const { data: serverAnnotations } = userAnnotationStore.useByBlock(blockId);
    // Merge: server rows first (so canonical id wins on collision),
    // then any extras the caller supplied for this render. Defensive
    // dedupe on id keeps a stub from re-appearing if both arrays
    // happen to reference the same row.
    const annotations = useMemo(() => {
        if (!extraAnnotations || extraAnnotations.length === 0) {
            return serverAnnotations;
        }
        const seen = new Set();
        const out = [];
        for (const row of serverAnnotations) {
            if (seen.has(row.id))
                continue;
            seen.add(row.id);
            out.push(row);
        }
        for (const row of extraAnnotations) {
            if (seen.has(row.id))
                continue;
            seen.add(row.id);
            out.push(row);
        }
        return out;
    }, [serverAnnotations, extraAnnotations]);
    // Iteration view: drop the row types that render in the parallel
    // `InlineAnnotationsLayer`. That layer owns every blend-mode rect
    // (text_highlight + text-range comments) because the container's
    // own `transform: scale(...)` auto-isolates the compositing group
    // and the multiply blend can't see the painted card text from
    // inside it. Avatars / pins / drawings / stamps / stickers stay
    // here where they ride the FLIP scale and pin against (0..1)
    // normalized coords.
    const positionedAnnotations = useMemo(() => {
        const out = [];
        for (const row of annotations) {
            if (row.type === "text_highlight")
                continue;
            if (row.type === "comment" && row.anchor?.type === "text_range")
                continue;
            out.push(row);
        }
        return out;
    }, [annotations]);
    // Resolve the selected drawing row (if any) for the chrome below.
    // Done here (rather than in the modal) because this container is
    // the shared coordinate frame: same `containerRef` for the drawing
    // body and the chrome means a mid-resize panel doesn't desync the
    // two.
    //
    // Ownership check is defense-in-depth: the per-row gate above
    // already prevents `selectAnnotation(non-owner-id)` from firing,
    // so `selectedId` shouldn't normally point at a non-owner drawing.
    // The extra check here covers the corner case where ownership
    // resolves later than the selection (e.g. attribution slot fills
    // mid-modal after the user already had a row selected).
    const selectedDrawing = useMemo(() => {
        if (mode !== "expanded")
            return undefined;
        const id = drawingSelection?.selectedId;
        if (!id)
            return undefined;
        const row = annotations.find((a) => a.id === id);
        if (!row || row.type !== "drawing")
            return undefined;
        if (isAnnotationMine(row, currentAccountId) === false)
            return undefined;
        return row;
    }, [annotations, drawingSelection?.selectedId, mode, currentAccountId]);
    const selectedFrame = useMemo(() => (selectedDrawing ? resolveDrawingFrame(selectedDrawing) : undefined), [selectedDrawing]);
    // Stable per-drawing selection bundle: same identity across
    // renders so DrawingAnnotationView's `memo` doesn't re-render on
    // every container tick.
    const onChromePatch = useCallback((patch) => {
        if (!selectedDrawing || !drawingSelection)
            return;
        drawingSelection.onPatch(selectedDrawing.id, patch);
    }, [drawingSelection, selectedDrawing]);
    const onChromeSettle = useCallback(() => {
        if (!selectedDrawing || !drawingSelection)
            return;
        drawingSelection.onSettle(selectedDrawing.id);
    }, [drawingSelection, selectedDrawing]);
    return (_jsxs("div", { ref: containerRef, className: `annotations-container annotations-container--mode-${mode}`, "aria-hidden": true, children: [positionedAnnotations.map((annotation) => {
                const entry = resolveAnnotationEntry(annotation);
                const position = positions?.get(annotation.id) ??
                    defaultPositionFor(annotation);
                // Ownership gate: only an annotation's owner can drag /
                // resize / rotate / select / delete it. Non-owner rows fall
                // through every conditional below as `undefined`, which
                // takes them down the registry's read-only paint path with
                // no gesture wiring, no selection chrome, and no pointer
                // events on their interactive surfaces. The static visual
                // (drawing strokes, emoji glyph, sticker tile) still
                // renders so contributors can see what each other made;
                // they just can't touch it.
                //
                // Accepted trade-off — non-owner drawing hover-dim:
                // `DrawingAnnotationView`'s hover producer (the one that
                // dims the right-rail author avatars to highlight the
                // matching contributor) only fires while the SVG paths are
                // `pointer-events: auto`, which the CSS gates on
                // `.annotation-drawing--interactive`. That class only
                // mounts when `drawingSelection !== undefined`, so non-
                // owner drawings — which now drop the prop here — lose
                // the hover affordance. The static author-avatar column
                // still surfaces who contributed; only the hover
                // correlation between a specific drawing and its author
                // avatar is gone for non-owner rows. Revisit if/when we
                // want to split interactivity from selectability (hover
                // producer + cursor change live on `interactive`, the
                // selection callbacks live on `selectable`).
                const isMine = isAnnotationMine(annotation, currentAccountId);
                // Gesture handlers only flow to mark-kind types in expanded
                // mode — read-only types don't take them, and regular mode
                // disables interactivity wholesale. Non-owner marks ride
                // the same `undefined` path.
                const handlers = mode === "expanded" && entry?.kind === "mark" && isMine
                    ? gestureHandlers
                    : undefined;
                // Drawing selection wiring: per-drawing `isSelected` flag
                // computed from the container-level `selectedId`. Only fed
                // to `drawing` rows in expanded mode AND owned by the
                // current user; other read-only types and non-owner
                // drawings get `undefined` and render as static SVG.
                const drawingSelectionProp = mode === "expanded" &&
                    annotation.type === "drawing" &&
                    drawingSelection !== undefined &&
                    isMine
                    ? {
                        isSelected: drawingSelection.selectedId === annotation.id,
                        onSelect: drawingSelection.onSelect,
                        onPatch: drawingSelection.onPatch,
                        onSettle: drawingSelection.onSettle,
                        containerRef,
                    }
                    : undefined;
                // Mark selection wiring: same idea as drawingSelection but
                // for mark-kind types. Per-mark `isSelected` derived from
                // the container-level shared `selectedId` so only one mark
                // (or one drawing) shows its chrome at a time. Gated on
                // ownership so non-owner marks render as inert glyphs.
                const markSelectionProp = mode === "expanded" &&
                    entry?.kind === "mark" &&
                    markSelection !== undefined &&
                    isMine
                    ? {
                        isSelected: markSelection.selectedId === annotation.id,
                        onSelect: markSelection.onSelect,
                    }
                    : undefined;
                return (_jsx(AnnotationView, { annotation: annotation, entry: entry, position: position, mode: mode, containerRef: containerRef, handlers: handlers, drawingSelection: drawingSelectionProp, markSelection: markSelectionProp }, annotation.id));
            }), liveOverlay, selectedDrawing && selectedFrame ? (_jsx(DrawingSelectionView, { annotationId: selectedDrawing.id, frame: selectedFrame, containerRef: containerRef, onPatch: onChromePatch, onSettle: onChromeSettle })) : null] }));
});
